/*
 * sw-cart-conversion.js (kaart #462, uitgebreid in kaart #587): kleine verbeteringen voor de winkelwagenlade.
 * Alle HTML en prijzen komen server-side uit Liquid; dit script haalt zelf geen winkelwagen op.
 *
 * <sw-cart-state> staat in cart-drawer.liquid buiten #CartDrawer, dus de lade vervangt hem nooit.
 *   - Meldt een nieuwe tekst van de verzendbalk via één vaste aria-live-regio. Die zet hij na elke
 *     verversing terug in het dialoogvenster (.drawer__inner, aria-modal): meldingen buiten een
 *     modaal venster slaan schermlezers vaak over, en een live-regio die net met tekst is ingevoegd
 *     ook. Daarom eerst leeg terugzetten en de tekst kort daarna invullen. meld() is er ook voor
 *     andere blokken (upsell): die tekst en een nieuwe verzendtekst gaan samen in één melding.
 *   - Kaart #587: toont de regel "Toegevoegd aan je winkelwagen" in de kop zodra de lade is ververst na een
 *     toevoeging (product-form.js en sw-atc-drawer.js publiceren cartUpdate vlak voor renderContents; de
 *     upsell zet de vlag zelf). De vlag staat op <cart-drawer> en overleeft de verversing; de regel
 *     verdwijnt weer bij het sluiten van de lade of bij de volgende verversing (aantal wijzigen).
 *   Kaart #466 (besluit 07-10-2026): geen kortingscodeveld meer in de lade, dus ook geen code en open
 *   stand bewaren en geen change-afscherming; de vorige versie staat in integration/kortingscode-later/.
 * <sw-cart-upsell> voegt het voorgestelde product toe en ververst de lade met dezelfde secties als
 *   de theme (cart-drawer en cart-icon-bubble). Daarna staat de focus op de nieuwe regel en meldt de
 *   live-regio wat er is toegevoegd. Zonder lade blijft het gewone formulier werken.
 * <sw-cart-countdown> (kaart #587) rekent de leverdag en de aftelling naar de cutoff van 16:00 opnieuw uit in
 *   de tijdzone Europe/Amsterdam, met dezelfde regel als snippets/sw-cart-delivery.liquid en het verzendbeleid:
 *   werkdag voor 16:00 = vandaag verzonden en morgen bezorgd; daarna of in het weekend = eerstvolgende werkdag
 *   verzonden en de dag erna bezorgd. De teksten komen als sjabloon uit de data-attributen.
 */
(() => {
  const BRONNEN_TOEGEVOEGD = ['product-form', 'sw-atc-drawer'];

  function markeerToegevoegd(drawer) {
    const lade = drawer || document.querySelector('cart-drawer');
    if (lade) lade.dataset.swAdded = String(Date.now());
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && BRONNEN_TOEGEVOEGD.includes(event.source)) markeerToegevoegd();
    });
  }

  if (!customElements.get('sw-cart-state')) {
    customElements.define('sw-cart-state', class extends HTMLElement {
      connectedCallback() {
        this.drawer = this.closest('cart-drawer');
        const inhoud = this.drawer?.querySelector('#CartDrawer');
        if (!inhoud || this.observer) return;
        this.melding = document.createElement('p');
        this.melding.className = 'visually-hidden';
        this.melding.setAttribute('role', 'status');
        this.melding.setAttribute('aria-live', 'polite');
        this.melding.dataset.swLadeMelding = '';
        this.plaatsMelding();
        this.verzendtekst = this.leesVerzendtekst();
        this.observer = new MutationObserver(() => this.naVerversen());
        this.observer.observe(inhoud, { childList: true, subtree: true });
        // Sluiten van de lade (class "active" valt weg): chip "Toegevoegd" weer verbergen. Alleen bij de overgang
        // open naar dicht: bij een toevoeging vanuit een lege wagen verdwijnt eerst "is-empty" terwijl de lade pas
        // een tik later "active" krijgt, en dat mag de chip niet verbergen.
        this.wasActief = this.drawer.classList.contains('active');
        this.sluitObserver = new MutationObserver(() => {
          const actief = this.drawer.classList.contains('active');
          if (this.wasActief && !actief) this.verbergToegevoegd();
          this.wasActief = actief;
        });
        this.sluitObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.sluitObserver?.disconnect();
        this.sluitObserver = null;
        clearTimeout(this.meldTimer);
        this.melding?.remove();
      }

      // Tekst voor schermlezers: eerst leeg, dan kort daarna invullen (anders vaak niet voorgelezen).
      kondig() {
        const tekst = [this.extra, this.wachtend].filter(Boolean).join(' ');
        clearTimeout(this.meldTimer);
        this.plaatsMelding();
        this.melding.textContent = '';
        this.meldTimer = setTimeout(() => {
          this.melding.textContent = tekst;
          this.extra = '';
          this.wachtend = '';
        }, 150);
      }

      meld(tekst) {
        this.extra = tekst;
        this.kondig();
      }

      // Zet de live-regio (zonder tekst) terug in het dialoogvenster als de lade hem heeft weggegooid.
      plaatsMelding() {
        const venster = this.drawer.querySelector('.drawer__inner') || this;
        if (this.melding.parentNode === venster) return false;
        this.melding.textContent = '';
        venster.append(this.melding);
        return true;
      }

      leesVerzendtekst() {
        const status = this.drawer.querySelector('[data-sw-cart-shipping-status]');
        return status ? status.textContent.trim() : '';
      }

      toonToegevoegd() {
        const regel = this.drawer.querySelector('[data-sw-cart-added]');
        // Dezelfde kop als daarnet (de mutatie kwam van de live-regio of de aftelling): niets doen, anders zou
        // een net getoonde regel meteen weer verdwijnen. Een nieuwe kop betekent een verversing van de lade.
        if (!regel || regel === this.toegevoegdRegel) return;
        this.toegevoegdRegel = regel;
        const stempel = Number(this.drawer.dataset.swAdded || 0);
        // De vlag is hoogstens een paar seconden oud (gezet vlak voor de verversing). Zonder verse vlag is dit
        // een gewone verversing (aantal wijzigen, verwijderen) en blijft de regel verborgen zoals gerenderd.
        if (stempel && Date.now() - stempel < 8000) {
          delete this.drawer.dataset.swAdded;
          regel.hidden = false;
        }
      }

      verbergToegevoegd() {
        const regel = this.drawer.querySelector('[data-sw-cart-added]');
        if (regel) regel.hidden = true;
      }

      naVerversen() {
        this.plaatsMelding();
        this.toonToegevoegd();
        const tekst = this.leesVerzendtekst();
        if (tekst && tekst !== this.verzendtekst) {
          this.wachtend = tekst;
          this.kondig();
        }
        this.verzendtekst = tekst;
      }
    });
  }

  // Na een upsell in de open lade (review B1). renderContents vervangt ook de knop met focus, en
  // open() zet de focus pas terug na een transitionend. Een lade die al open is, krijgt geen
  // transitie: de luisteraar van open() blijft dan wachten en trekt bij het sluiten de focus terug
  // de lade in. Daarom die gebeurtenis hier zelf afvuren en de focus op de nieuwe regel zetten.
  function naToevoegen(drawer, variantId, wasOpen, tekst) {
    if (wasOpen) drawer.dispatchEvent(new Event('transitionend'));
    const venster = drawer.querySelector('.drawer__inner');
    const regel = drawer.querySelector(`#CartDrawer .cart-item__name[href*="variant=${variantId}"]`);
    const doel = regel || venster;
    if (doel) {
      if (typeof trapFocus === 'function' && venster) trapFocus(venster, doel);
      else doel.focus();
    }
    if (tekst) drawer.querySelector('sw-cart-state')?.meld?.(tekst);
  }

  if (!customElements.get('sw-cart-upsell')) {
    customElements.define('sw-cart-upsell', class extends HTMLElement {
      connectedCallback() {
        if (this.onSubmit) return;
        this.onSubmit = this.voegToe.bind(this);
        this.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.onSubmit);
        this.onSubmit = null;
      }

      async voegToe(event) {
        const form = event.target;
        const drawer = this.closest('cart-drawer');
        // Zonder de lade van de theme blijft de gewone POST naar /cart/add werken.
        if (!drawer?.renderContents || !drawer.getSectionsToRender || !form.matches('form')) return;
        event.preventDefault();
        if (this.bezig) return;
        this.bezig = true;
        const knop = form.querySelector('button[type="submit"]');
        const fout = this.querySelector('[role="alert"]');
        knop.disabled = true;
        knop.setAttribute('aria-busy', 'true');
        fout.hidden = true;
        const secties = drawer.getSectionsToRender().map((sectie) => sectie.id);
        const body = new FormData(form);
        body.set('sections', secties.join(','));
        body.set('sections_url', window.location.pathname);
        // Na succes verdwijnt dit blok; de focus gaat dan terug naar het winkelwagenicoon.
        drawer.setActiveElement?.(document.getElementById('cart-icon-bubble'));
        try {
          const antwoord = await fetch(form.action, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await antwoord.json();
          if (data.status) {
            // Shopify weigert expliciet (bijvoorbeeld uitverkocht): opnieuw proberen mag.
            fout.textContent = this.dataset.error;
            fout.hidden = false;
            this.bezig = false;
            knop.disabled = false;
            return;
          }
          if (!antwoord.ok || secties.some((id) => typeof data.sections?.[id] !== 'string' || !data.sections[id].trim())) {
            throw new Error('lade niet ververst');
          }
          const variantId = body.get('id');
          const wasOpen = drawer.classList.contains('active');
          const tekst = this.dataset.added;
          markeerToegevoegd(drawer);
          drawer.renderContents(data);
          // renderContents opent de lade in een setTimeout; deze komt daarna.
          setTimeout(() => naToevoegen(drawer, variantId, wasOpen, tekst));
        } catch {
          // Onbekend of het gelukt is: niet opnieuw versturen, wel de weg naar de winkelwagen tonen.
          fout.textContent = this.dataset.uncertain;
          fout.hidden = false;
          this.querySelector('.sw-cart-upsell__recover').hidden = false;
        } finally {
          knop.removeAttribute('aria-busy');
        }
      }
    });
  }

  if (!customElements.get('sw-cart-countdown')) {
    customElements.define('sw-cart-countdown', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.market !== 'nl') return;
        this.dag = this.querySelector('[data-sw-dl-day]');
        this.teller = this.querySelector('[data-sw-dl-count]');
        if (!this.dag) return;
        this.vernieuw();
        this.timer = setInterval(() => this.vernieuw(), 30000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        this.timer = null;
      }

      // Datum en tijd in Europe/Amsterdam, onafhankelijk van de tijdzone van de bezoeker.
      nuAmsterdam() {
        const delen = new Intl.DateTimeFormat('en-GB', {
          timeZone: this.dataset.tz || 'Europe/Amsterdam',
          year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false,
        }).formatToParts(new Date());
        const v = {};
        delen.forEach((deel) => { v[deel.type] = deel.value; });
        return { jaar: +v.year, maand: +v.month, dag: +v.day, uur: (+v.hour) % 24, minuut: +v.minute };
      }

      vernieuw() {
        let nu;
        try { nu = this.nuAmsterdam(); } catch { return; }
        const DAG = 86400000;
        const cutoff = Number(this.dataset.cutoff || 16);
        const dagen = (this.dataset.days || '').split(',');
        const maanden = (this.dataset.months || '').split(',');
        // Kalenderdagen als UTC-middernacht, zodat optellen van dagen geen last heeft van zomertijd.
        const vandaag = Date.UTC(nu.jaar, nu.maand - 1, nu.dag);
        const weekdag = new Date(vandaag).getUTCDay();
        const werkdag = weekdag >= 1 && weekdag <= 5;
        const voorCutoff = werkdag && nu.uur < cutoff;
        // Verwerkdag: vandaag op een werkdag voor de cutoff, anders de eerstvolgende werkdag; bezorging de dag erna.
        let verwerk = vandaag;
        if (!voorCutoff) {
          verwerk = vandaag + DAG;
          while ([0, 6].includes(new Date(verwerk).getUTCDay())) verwerk += DAG;
        }
        const leverTs = verwerk + DAG;
        const lever = new Date(leverTs);
        const datum = `${dagen[lever.getUTCDay()] || ''} ${lever.getUTCDate()} ${maanden[lever.getUTCMonth()] || ''}`.trim();
        const sjabloon = leverTs === vandaag + DAG ? this.dataset.tplTomorrow : this.dataset.tplDay;
        this.zet(this.dag, (sjabloon || '').replace('%DATE%', datum));
        if (!this.teller) return;
        if (this.dataset.countdown !== 'true' || !voorCutoff) {
          this.teller.hidden = true;
          return;
        }
        const rest = cutoff * 60 - (nu.uur * 60 + nu.minuut);
        const uren = Math.floor(rest / 60);
        const minuten = rest % 60;
        const tijd = uren > 0
          ? (this.dataset.tplHm || '').replace('%H%', String(uren)).replace('%M%', String(minuten))
          : (this.dataset.tplM || '').replace('%M%', String(minuten));
        this.zet(this.teller, (this.dataset.tplCountdown || '').replace('%TIME%', tijd));
        this.teller.hidden = false;
      }

      zet(element, tekst) {
        if (element.textContent !== tekst) element.textContent = tekst;
      }
    });
  }
})();
